import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import { HeroImage } from 'components/shared/HeroImage';
import { navScrollTo, useScrollerNav } from 'components/shared/ScrollerNav';
import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import Stats from 'components/press/Stats';
import CardGridSection from 'components/press/CardGridSection';
import PressCard from 'components/press/PressCard';
import { ArticleCard, PressReleaseCard } from 'components/press/PressCard/PressCard';
import { PressNavId, PRESS_PAGE_STRINGS } from './press.strings';
import heroJPG from './heroImages/hero-2800w.jpg';
import heroAVIF from './heroImages/hero-2800w.avif';
import heroWEBP from './heroImages/hero-2800w.webp';
import heroMobileJPG from './heroImages/hero-780w.jpg';
import heroMobileAVIF from './heroImages/hero-780w.avif';
import heroMobileWEBP from './heroImages/hero-780w.webp';
import * as styles from './styles/styles.module.scss';
import OurLeadershipTeam from './OurLeadershipTeam';
import MedicalAdvisor from './MedicalAdvisor';
import OurMission from './OurMission';
import articles from './cardData/articles';
import pressReleases from './cardData/pressReleases';
import Highlights from './Highlights';
import ContactUs from './ContactUs';

// Figma link: https://www.figma.com/file/XrlDM2ZdWXmlnl7ZXUicGn/Rebrand%E2%80%93Press%2FAbout%2F404?node-id=1304%3A3492&t=JV5zatKmMcXo87qe-0

const PressContent = () => {
  const intl = useIntl();

  const sections = [
    { id: 'about-alltrails', title: intl.formatMessage({ defaultMessage: 'About AllTrails' }) },
    { id: 'leadership-team', title: intl.formatMessage({ defaultMessage: 'Leadership team' }) },
    { id: 'highlights', title: intl.formatMessage({ defaultMessage: 'Highlights' }) },
    { id: 'in-the-news', title: intl.formatMessage({ defaultMessage: 'In the news' }) },
    { id: 'press-room', title: intl.formatMessage({ defaultMessage: 'Press room' }) },
    { id: 'press-kit', title: intl.formatMessage({ defaultMessage: 'Press kit' }) }
  ];

  const ctaInfo = {
    linkInfo: { href: 'mailto:press@alltrails.com' },
    target: '_blank',
    testId: 'contact-us',
    text: <FormattedMessage defaultMessage="Contact us" />
  };

  const { renderNavBar, renderSection } = useScrollerNav(<FormattedMessage defaultMessage="Press" />, sections, ctaInfo);

  const {
    formattedDefaultMessages: { HERO_SUBHEADER_TEXT, HERO_HEADER_TEXT, HERO_ALT }
  } = useFormatMessage(PRESS_PAGE_STRINGS);

  return (
    <div className={styles.container}>
      <HeroImage
        backgroundImage={{
          avif: { src: heroAVIF, mobileSrc: heroMobileAVIF },
          webp: { src: heroWEBP, mobileSrc: heroMobileWEBP },
          jpg: { src: heroJPG, mobileSrc: heroMobileJPG },
          alt: HERO_ALT
        }}
        headerText={HERO_HEADER_TEXT}
        headerSubText={HERO_SUBHEADER_TEXT}
        onArrowClick={() => navScrollTo(PressNavId.About)}
        textContainerClassName={styles.textContainer}
      />
      {renderNavBar()}
      {renderSection(
        sections[0].id,
        <div id={PressNavId.About}>
          <Stats />
        </div>
      )}
      {renderSection(
        sections[1].id,
        <>
          <OurLeadershipTeam />
          <MedicalAdvisor />
        </>
      )}
      {renderSection(sections[2].id, <Highlights />)}
      {renderSection(
        sections[3].id,
        <CardGridSection
          header={<FormattedMessage defaultMessage="In the news" />}
          subheader={
            <FormattedMessage defaultMessage="From outdoor magazines to travel publications, see what others are saying about us and catch our latest updates below." />
          }
        >
          {articles.map(({ title, logoSrc, company, date, href }: ArticleCard, index) => (
            <PressCard cardType="article" logoSrc={logoSrc} company={company} title={title} date={date} href={href} key={`${title}-${index}`} />
          ))}
        </CardGridSection>
      )}

      <div className={styles.cardSectionsDivider} />

      {/* Press Releases */}
      {renderSection(
        sections[4].id,
        <CardGridSection header={<FormattedMessage defaultMessage="Press room" />}>
          {pressReleases.map(({ title, date, modalContent }: PressReleaseCard, index) => (
            <PressCard cardType="press-release" title={title} date={date} modalContent={modalContent} key={`${title}-${index}`} />
          ))}
        </CardGridSection>
      )}
      {renderSection(sections[5].id, <ContactUs />)}
      <OurMission />
    </div>
  );
};

export default PressContent;
